import React, { useEffect, useState } from "react";
import { ShieldCheck, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Spinner } from "./common";
import { api, errMsg } from "../lib/api";
import { staffLabelText } from "./StaffAvatar";
import { ModuleAccessEditor } from "./ModuleAccessEditor";

// Per-user menu access editor. Reused by Users & Staff (admin) and My Team (parents).
// When the backend returns `max_levels` (parent editing a sub-user), options above the
// parent's own effective level are disabled.
export function UserAccessDialog({ code, user, open, onOpenChange, onSaved }) {
  const [data, setData] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !user?.id) return;
    setData(null);
    api.get(`/t/${code}/role-modules/user/${user.id}`)
      .then((r) => {
        setData(r.data);
        const init = {};
        r.data.registry.forEach((m) => {
          init[m.key] = r.data.overrides[m.key] || "inherit";
          // Phase 152: sub-feature overrides ("module.feature")
          (m.features || []).forEach((f) => { const k = `${m.key}.${f.key}`; init[k] = r.data.overrides[k] || "inherit"; });
        });
        setOverrides(init);
        setDirty(false);
      })
      .catch((e) => { toast.error(errMsg(e)); onOpenChange(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user?.id, code]);

  const save = async () => {
    setBusy(true);
    try {
      const body = {};
      Object.entries(overrides).forEach(([k, v]) => { if (v !== "inherit") body[k] = v; });
      await api.put(`/t/${code}/role-modules/user/${user.id}`, { overrides: body });
      toast.success(`Menu access saved for ${user.name}`);
      setDirty(false);
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const caps = data?.max_levels || null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="user-access-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-base">
            <ShieldCheck className="h-4 w-4" /> Menu access — {staffLabelText(user?.name, user?.employee_id)}
          </DialogTitle>
        </DialogHeader>
        {!data ? <Spinner /> : (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              "Inherit" follows the role default. Overrides apply to this user only. Expand a module to override its sub-tabs / features.
              {caps ? " You cannot grant a sub-user more access than you hold yourself." : ""}
            </p>
            <ModuleAccessEditor modules={data.registry} mode="user" baseline={data.role_defaults} caps={caps}
              getValue={(k) => overrides[k]} onChange={(k, v) => { setOverrides((o) => ({ ...o, [k]: v })); setDirty(true); }}
              testId="user-access" compact />
            <div className="flex justify-end pt-2">
              <Button onClick={save} disabled={!dirty || busy} data-testid="user-access-save-button">
                <Save className="h-4 w-4 mr-2" /> {busy ? "Saving..." : "Save access"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
